import React from 'react';
import { createPortal } from 'react-dom';
import { useModalScrollLock } from '../../../hooks/useModalScrollLock';
import { ModalActionError } from '../../../ui/ModalActionError';
import { CloseIcon } from '../../../ui/CloseIcon';
import { captureOpener, holdFocusOnBackdrop, tabbableIn, trapTabKey } from '../../../components/receiving/dialogDom';

type TransferRecordDialogProps = {
  title: string;
  eyebrow?: string;
  subtitle?: React.ReactNode;
  onClose: () => void;
  /** While true, Escape and the X do nothing (an action is saving). */
  busy?: boolean;
  /** Shown above the footer actions, where the person pressed (role="alert"). */
  error?: string | null;
  errorTitle?: string;
  /** The footer's actions; the primary one last. */
  footer: React.ReactNode;
  /** Wraps the body and footer in a form, so Enter submits. */
  onSubmit?: () => void;
  size?: 'md' | 'lg';
  className?: string;
  children: React.ReactNode;
};

/**
 * The record's action dialogs (ship, cancel, dates, lines, stops, shipments) share this frame: the
 * house .modal, focus moved in and trapped, Escape and the X close it, and the error sits next to
 * the actions. A press on the backdrop never closes it, so typed values are never lost.
 */
export default function TransferRecordDialog(props: TransferRecordDialogProps) {
  if (typeof document === 'undefined') return null;
  return createPortal(<TransferRecordDialogFrame {...props} />, document.body);
}

function TransferRecordDialogFrame({
  title,
  eyebrow,
  subtitle,
  onClose,
  busy = false,
  error,
  errorTitle,
  footer,
  onSubmit,
  size = 'md',
  className,
  children,
}: TransferRecordDialogProps) {
  const titleId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const [opener] = React.useState(captureOpener);
  const latest = React.useRef({ onClose, busy });
  latest.current = { onClose, busy };

  useModalScrollLock(true);

  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog || dialog.contains(document.activeElement)) return;
      const preferred = dialog.querySelector<HTMLElement>('[data-autofocus="true"]');
      const body = dialog.querySelector<HTMLElement>('.modal__body');
      const first = body ? tabbableIn(body)[0] : undefined;
      (preferred ?? first ?? headingRef.current ?? dialog).focus({ preventScroll: true });
    });
    return () => {
      window.cancelAnimationFrame(frame);
      const active = document.activeElement;
      if (opener?.isConnected && (!active || active === document.body)) opener.focus({ preventScroll: true });
    };
  }, [opener]);

  const handleClose = React.useCallback(function handleClose() {
    if (!latest.current.busy) latest.current.onClose();
  }, []);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const dialog = dialogRef.current;
    if (!dialog || !(event.target instanceof Node) || !dialog.contains(event.target)) return;
    if (event.key === 'Escape') {
      // A native select and an open combobox list handle their own Escape.
      const active = document.activeElement;
      if (event.defaultPrevented || active instanceof HTMLSelectElement) return;
      if (active instanceof HTMLElement && active.matches('[role="combobox"][aria-expanded="true"]')) return;
      event.preventDefault();
      // Page and header listeners (the record header's menu) must not also see this Escape.
      event.stopPropagation();
      handleClose();
      return;
    }
    trapTabKey(event, dialog);
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!busy) onSubmit?.();
  }

  const content = (
    <>
      <div className="modal__body">
        <div className="modal__body-content transfer-record-dialog__content">{children}</div>
      </div>
      <ModalActionError message={error ?? null} title={errorTitle} />
      <footer className="modal__footer transfer-record-dialog__footer">{footer}</footer>
    </>
  );

  return (
    <div className="modal-overlay transfer-record-dialog-overlay" onMouseDown={holdFocusOnBackdrop}>
      <div
        ref={dialogRef}
        className={['modal', 'transfer-record-dialog', `transfer-record-dialog--${size}`, className].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <header className="modal__header transfer-record-dialog__header">
          <div className="transfer-record-dialog__heading">
            {eyebrow ? <p className="transfer-record-dialog__eyebrow">{eyebrow}</p> : null}
            <h2 ref={headingRef} id={titleId} className="modal__title transfer-record-dialog__title" tabIndex={-1}>
              {title}
            </h2>
            {subtitle ? <p className="transfer-record-dialog__subtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="btn-close" aria-label="Close" title="Close" onClick={handleClose} disabled={busy}>
            <CloseIcon />
          </button>
        </header>
        {onSubmit ? (
          <form className="transfer-record-dialog__form" noValidate onSubmit={handleSubmit}>
            {content}
          </form>
        ) : content}
      </div>
    </div>
  );
}
